import { createTheme, Components, Theme } from '@mui/material/styles';
import backgroundImage from './12141.jpg';

// Color palette
const colors = {
  background: {
    primary: '#FFFFFF', //'#FAF1E6',    // Soft beige
    glass: 'rgba(255, 255, 255, 0.15)',
    frosted: 'rgba(200, 225, 255, 0.2)',
    gradient: {
      light: ' background: linear-gradient(135deg,  #F8E8EB 0%,  #FBE9ED 25%, #FDF3F5 50%, #FAEEF0 75%, #FFFFFF 100% )', //Very light pink 
      // light: 'linear-gradient(135deg,     #B76E79 0%,     #D991A3 25%,#E9B2C0 50%, #C97F8D 75%, #FBE3E9 100% )',
      // light: 'linear-gradient(135deg, #B07651 0%, #D29C78 25%, #E9C2A0 50%, #B87F5B 75%, #FBE9CD 100%)', // Orange
      dark: 'linear-gradient(135deg, #2C3E50 0%, #34495E 25%, #415B76 50%, #526A82 75%, #607D8B 100%)'  // Colder blues
    },
  },
  primary: {
    main: '#2F4F3B',          // Deep Teal
    // main: '#B83232',       // Rich burgundy
    // dark: '#932828',       // Darker burgundy
    dark: '#5B8C6A',          // Lighter Shade of Teal
    light: 'rgba(184, 50, 50, 0.1)', // Light burgundy
    darkMode: {
      main: '#4A90E2',     // Cool blue
      dark: '#2171CD',     // Darker blue
      light: 'rgba(74, 144, 226, 0.1)', // Light blue
      error: '#5C6BC0',    // Cold purple for error/delete actions
      warning: '#4FB3BF',  // Cold cyan for warnings
      success: '#48A9A6'   // Cold teal for success
    }
  },
  secondary: {
    main: '#2C3E50',       // Dark navy blue
    light: '#4B4B4B',      // Dark grey
  },
  text: {
    primary: '#2A2A2A',    // Dark charcoal
    secondary: '#4B4B4B',  // Dark grey
    light: '#FFFFFF',      // White
  },
  accent: {
    gold: '#FFC107',       // Bright gold
    copper: '#B87333',     // Copper
    wood: '#7D4B2A',       // Wood brown
    leather: '#DCA679',    // Leather tan
  }
};

export const baseTheme = createTheme({
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  palette: {
    primary: {
      main: colors.primary.main,
      dark: colors.primary.dark,
      light: colors.primary.light,
      contrastText: colors.text.light,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      contrastText: colors.text.light,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    background: {
      default: colors.background.primary,
      paper: colors.background.glass,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: colors.background.gradient.light,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          '@media (max-width: 600px)': {
            padding: '16px 8px',
          },
        },
        // Add these styles to prevent mobile Chrome from zooming on input focus
        'input, textarea, select, button': {
          fontSize: '16px', // Minimum font size to prevent zoom
          touchAction: 'manipulation', // Prevent unintended zooming
        },
        // Ensure all form elements have proper font size
        '.MuiInputBase-input, .MuiOutlinedInput-input, .MuiFilledInput-input, .MuiInput-input': {
          fontSize: '16px !important',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '0 8px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: colors.background.glass,
          backdropFilter: 'blur(20px)',
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.18)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          transition: 'all 0.3s ease',
          '@media (max-width: 600px)': {
            padding: '16px',
            borderRadius: 12,
          },
          '&.secondary-paper': {
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            boxShadow: 'none',
            padding: '16px 0',
          },
          '&.schedule-container': {
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            boxShadow: 'none',
            padding: '24px',
            '& .MuiTabs-root': {
              marginBottom: '24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            },
          },
          '&.schedule-content': {
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            boxShadow: 'none',
            padding: 0,
          },
          '&.schedule-item': {
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 2px 8px rgba(31, 38, 135, 0.05)',
            marginBottom: '12px',
            padding: '16px',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            fontSize: '0.875rem',
            width: '100%',
            marginTop: '8px',
            marginBottom: '8px',
            '&.dashboard-nav-button': {
              minWidth: 0,
              padding: '8px 12px',
              whiteSpace: 'nowrap',
              width: 'auto',
            },
          },
          '&:hover': {
            boxShadow: '0 4px 15px rgba(184, 50, 50, 0.2)',
          },
        },
        contained: {
          '@media (max-width: 600px)': {
            '&.MuiButton-containedPrimary + .MuiButton-containedPrimary': {
              marginTop: '12px',  // Add spacing between stacked primary buttons
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiInputBase-root': {
              fontSize: '16px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px',
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h4: {
          '@media (max-width: 600px)': {
            fontSize: '1.5rem',
          },
        },
        h6: {
          '@media (max-width: 600px)': {
            fontSize: '1.1rem',
          },
        },
        body1: {
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            minWidth: 'auto',
            padding: '6px 12px',
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '&.button-container': {
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
            },
            '&.section-header': {  // New style for section headers
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              '& .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
                textAlign: 'center',
                width: '100%',
              },
              '& .action-button': {
                width: '100%',
                marginTop: '8px',
              },
            },
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: {
          '@media (max-width: 600px)': {
            '&.dashboard-nav': {
              padding: '0 8px',
              '& .MuiGrid-item': {
                paddingLeft: '4px',
                paddingRight: '4px',
              },
            },
          },
        },
        item: {
          '@media (max-width: 600px)': {
            '&.button-grid-item': {
              width: '100%',
              flexBasis: '100%',
              maxWidth: '100%',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  } as Components<Theme>,
} as const);

export const lightTheme = createTheme(baseTheme);

export const darkTheme = createTheme({
  ...baseTheme,  // Spread all base theme properties
  palette: {
    ...baseTheme.palette,  // Keep all base palette properties
    mode: 'dark',
    primary: {
      main: '#4caf50',      // Light green
      light: '#81c784',     // Lighter shade of green
      dark: '#388e3c',      // Darker shade of green
    },
    background: {
      default: '#000000',   // Pure black background
      paper: 'rgba(255, 255, 255, 0.15)',  // Keep glass effect consistent
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    action: {
      active: '#81c784',    // Lighter green for icons
      hover: 'rgba(129, 199, 132, 0.08)',
      selected: 'rgba(129, 199, 132, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  // Remove all component overrides and use baseTheme components
  components: baseTheme.components,
});

export { colors };
export default lightTheme; 